/**
 * This module contains the shared order form logic for all distribution platforms.
 * Do not import the inidividual order form components in this file to prevent
 * circular dependencies. Import this file instead to use the shared order form logic.
 */
import { useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormTrigger,
} from 'react-hook-form';
import { useFetcher, useLocation } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Caution } from 'src/components/icons/caution';
import { Trash } from 'src/components/icons/trash';
import {
  Ethnicity,
  OrderBaseFieldsFragment,
  PatientStatusAtBiopsy,
  TStage,
} from 'src/types';
import { OrderAction } from 'src/types/loaders';
import { TStageUtils } from 'src/utils';

export interface FormFieldProps<T extends FieldValues> {
  control: Control<T>;
  errors: FieldErrors<T>;
  trigger?: UseFormTrigger<T>;
  getValues?: () => T;
}

export const GLEASON_SCORE_OPTIONS = [
  { value: '3', name: '3' },
  { value: '4', name: '4' },
  { value: '5', name: '5' },
];

// Hide T2 for prostate biopsy orders because it's only applicable for Post RP.
export const PROSTATE_BIOPSY_T_STAGES: TStage[] = [
  TStage.T1A,
  TStage.T1B,
  TStage.T1C,
  TStage.T2A,
  TStage.T2B,
  TStage.T2C,
  TStage.T3A,
  TStage.T3B,
  TStage.T4,
];

export const PROSTATE_POST_RP_T_STAGES: TStage[] = [
  TStage.T2,
  TStage.T3A,
  TStage.T3B,
  TStage.T4,
];

export function canUseTStageNotProvided(
  isCSOrSalesUser: boolean,
  allowTStageNotProvided: boolean,
): boolean {
  return isCSOrSalesUser || allowTStageNotProvided;
}

export function getProstateBiopsyTStages(
  isCSOrSalesUser: boolean,
  includeNotProvided: boolean,
): TStage[] {
  const stages = isCSOrSalesUser
    ? [...PROSTATE_BIOPSY_T_STAGES]
    : PROSTATE_BIOPSY_T_STAGES.filter((stage) => stage !== TStage.T3);

  if (includeNotProvided) {
    return [TStage.NotProvided, ...stages];
  }

  return stages;
}

export function getProstateBiopsyTStageOptions(
  isCSOrSalesUser: boolean,
  includeNotProvided: boolean,
) {
  return getProstateBiopsyTStages(isCSOrSalesUser, includeNotProvided).map(
    (value) => ({
      value,
      name: TStageUtils.getProstateBiopsyTStageName(value),
    }),
  );
}

export function getProstatePostRpTStages(includeNotProvided = false): TStage[] {
  if (includeNotProvided) {
    return [TStage.NotProvided, ...PROSTATE_POST_RP_T_STAGES];
  }
  return [...PROSTATE_POST_RP_T_STAGES];
}

export function getProstatePostRpTStageOptions(includeNotProvided = false) {
  return getProstatePostRpTStages(includeNotProvided).map((value) => ({
    value,
    name: TStageUtils.getProstatePostRpTStageName(value),
  }));
}

// Ethnicity options
export const ETHNICITY_OPTIONS = [
  {
    value: Ethnicity.AmericanIndianOrAlaskaNative,
    name: 'American Indian or Alaska Native',
  },
  { value: Ethnicity.Asian, name: 'Asian' },
  {
    value: Ethnicity.BlackOrAfricanAmerican,
    name: 'Black or African American',
  },
  { value: Ethnicity.HispanicOrLatino, name: 'Hispanic or Latino' },
  {
    value: Ethnicity.NativeHawaiianOrOtherPacificIslander,
    name: 'Native Hawaiian or Other Pacific Islander',
  },
  { value: Ethnicity.White, name: 'White' },
  { value: Ethnicity.Other, name: 'Other' },
];

// Patient Status options
export const PATIENT_STATUS_OPTIONS = [
  {
    value: PatientStatusAtBiopsy.HospitalInpatient,
    name: 'Hospital Inpatient',
  },
  {
    value: PatientStatusAtBiopsy.HospitalOutpatient,
    name: 'Hospital Outpatient',
  },
  {
    value: PatientStatusAtBiopsy.NonHospitalOutpatient,
    name: 'Non-Hospital Outpatient',
  },
  {
    value: PatientStatusAtBiopsy.NotProvided,
    name: 'Not Provided',
  },
];

// Pure function for processing fetcher response data
// This can be easily unit tested without React dependencies
export const processFetcherResponse = (
  fetcherData: unknown,
  fetcherState: string,
) => {
  // Return early if conditions aren't met
  if (!fetcherData || fetcherState !== 'idle') {
    return null;
  }

  const actionResult = fetcherData as {
    action: OrderAction;
    success: boolean;
    error?: string;
  };

  switch (actionResult.action) {
    case OrderAction.CREATE:
      if (!actionResult.success) {
        return {
          type: 'error',
          message:
            actionResult.error ||
            'The order could not be created at this time. Please try again later.',
        };
      }
      return null;

    case OrderAction.UPDATE:
    case OrderAction.UPDATE_REJECTED_ORDER:
      if (actionResult.success) {
        return {
          type: 'success',
          message: 'Order updated successfully.',
          closeDialog: true,
        };
      } else {
        return {
          type: 'error',
          message:
            actionResult.error ||
            'The order could not be updated at this time. Please try again later.',
        };
      }

    case OrderAction.SUBMIT:
      if (!actionResult.success) {
        return {
          type: 'error',
          message:
            actionResult.error ||
            'The order could not be submitted at this time. Please try again later.',
        };
      }
      return null;

    case OrderAction.RESUBMIT:
      if (!actionResult.success) {
        return {
          type: 'error',
          message:
            actionResult.error ||
            'The order could not be resubmitted at this time. Please try again later.',
        };
      }
      return null;

    case OrderAction.CANCEL:
      if (actionResult.success) {
        return {
          type: 'success',
          message: 'Order cancelled successfully.',
        };
      } else {
        return {
          type: 'error',
          message:
            'The order could not be cancelled at this time. Please try again later.',
        };
      }

    case OrderAction.REVISE_REPORT:
      if (!actionResult.success) {
        return {
          type: 'error',
          message:
            'The report could not be revised at this time. Please try again later.',
        };
      }
      return null;

    case OrderAction.DOWNLOAD_REPORT:
    case OrderAction.DOWNLOAD_CLINICAL_DECISION_SUPPORT:
    case OrderAction.DOWNLOAD_COMBINED_REPORT:
      if (!actionResult.success) {
        return {
          type: 'error',
          message:
            actionResult.error ||
            'The report could not be downloaded at this time. Please try again later.',
        };
      }
      return null;
    default:
      return null;
  }
};

// Helper function to convert empty strings to undefined
export const emptyToUndefined = (
  value: string | number,
): string | number | undefined => {
  return value === '' ? undefined : value;
};

// Helper function to safely parse integers
export const safeParseInt = (value: string | undefined): number | undefined => {
  return !value || value === '' ? undefined : parseInt(value);
};

// Helper function to safely parse floats
export const safeParseFloat = (
  value: string | undefined,
): number | undefined => {
  return !value || value === '' ? undefined : parseFloat(value);
};

// Helper function to handle optional values
export const safeEmptyToUndefined = (
  value: string | undefined,
): string | undefined => {
  return !value || value === '' ? undefined : value;
};

// Utility function to submit form actions
export const submitOrderAction = (
  submit: ReturnType<typeof useFetcher>['submit'],
  action: OrderAction,
  location: ReturnType<typeof useLocation>,
  orderData?: OrderBaseFieldsFragment,
  options?: Record<string, unknown>,
) => {
  const formData = new FormData();
  formData.append('action', action);

  if (orderData) {
    formData.append('orderData', JSON.stringify(orderData));
  }

  if (options) {
    formData.append('options', JSON.stringify(options));
  }

  // Safeguard against form submission on non-order form pages.
  if (
    !location.pathname.includes('edit') &&
    !location.pathname.includes('new')
  ) {
    return;
  }

  submit(formData, {
    method: 'post',
    action: location.pathname,
  });
};

/** Props for the FormActions component. */
export interface FormActionsProps {
  // ── Button visibility ────────────────────────────────────────────────────
  showSubmitButton: boolean;
  /** SAVE button shown when a CS/Sales user is editing a QC Pending order. */
  showSaveButton: boolean;
  /** DISCARD + SAVE AS DRAFT buttons shown for draft / unsaved orders. */
  showDraftActionButtons: boolean;

  // ── Action callbacks ─────────────────────────────────────────────────────
  /**
   * Called when the SUBMIT button is clicked. Should run form validation and
   * return true if the form is valid (confirmation dialog will then open).
   */
  onValidateBeforeSubmit: () => Promise<boolean>;
  /** Called after the user confirms in the submit confirmation dialog. */
  onConfirmSubmit: () => Promise<void>;
  /** Called when the user clicks SAVE AS DRAFT (no confirmation needed). */
  onSaveAsDraft: () => Promise<void>;
  onDiscard: () => Promise<void>;
  /**
   * When false (unsaved / new order) DISCARD runs immediately without a
   * confirmation dialog. When true a confirmation dialog is shown first.
   */
  requireDiscardConfirmation: boolean;
  onDiscardSuccess?: () => void;

  /** Called when the user clicks SAVE. */
  onSave?: () => Promise<void>;
}

/**
 * Order form action buttons and their associated confirmation dialogs.
 * Accepts simple boolean flags for visibility and direct action callbacks —
 * all business logic for computing those flags lives in each form component.
 */
export const FormActions = ({
  showSubmitButton,
  showSaveButton,
  showDraftActionButtons,
  onValidateBeforeSubmit,
  onConfirmSubmit,
  onSaveAsDraft,
  onDiscard,
  requireDiscardConfirmation,
  onDiscardSuccess,
  onSave,
}: FormActionsProps) => {
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const handleSubmitClick = async () => {
    const isValid = await onValidateBeforeSubmit();
    if (isValid) setSubmitDialogOpen(true);
  };

  const handleSaveClick = async () => {
    await onSave?.();
  };

  const handleDiscardClick = async () => {
    if (!requireDiscardConfirmation) {
      await onDiscard();
    } else {
      setDiscardDialogOpen(true);
    }
  };

  const handleConfirmSubmit = async () => {
    setSubmitDialogOpen(false);
    await onConfirmSubmit();
  };

  const handleConfirmDiscard = async () => {
    setDiscardDialogOpen(false);
    await onDiscard();
    onDiscardSuccess?.();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'stretch', sm: 'space-between' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 0.25, sm: 2 },
            order: { xs: 2, sm: 1 },
          }}
        >
          {showDraftActionButtons && (
            <>
              <Button
                type='button'
                variant='text'
                color='primary'
                onClick={handleDiscardClick}
                size='large'
                sx={{
                  minHeight: { xs: '44px', sm: 'auto' },
                }}
              >
                DISCARD
              </Button>
              <Button
                type='button'
                variant='text'
                color='primary'
                onClick={() => onSaveAsDraft()}
                size='large'
                sx={{
                  minHeight: { xs: '44px', sm: 'auto' },
                }}
              >
                SAVE AS DRAFT
              </Button>
            </>
          )}
        </Box>
        {showSubmitButton && (
          <Button
            variant='contained'
            color='primary'
            size='large'
            data-testid='order-form-submit-button'
            sx={{
              width: { xs: '100%', sm: '160px' },
              minHeight: { xs: '48px', sm: 'auto' },
              order: { xs: 1, sm: 2 },
            }}
            onClick={handleSubmitClick}
          >
            SUBMIT
          </Button>
        )}
        {showSaveButton && (
          <Button
            variant='contained'
            color='primary'
            size='large'
            sx={{
              width: { xs: '100%', sm: '160px' },
              minHeight: { xs: '48px', sm: 'auto' },
              order: { xs: 1, sm: 2 },
            }}
            onClick={handleSaveClick}
            data-testid='order-form-save-button'
          >
            SAVE
          </Button>
        )}
      </Box>
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, I CONFIRM',
          secondaryButtonText: 'NO',
          title: 'Confirm Submission?',
          subtitle:
            'I have reviewed and confirmed the information on the order is correct.',
          type: FeedbackType.Primary,
          icon: Caution,
        }}
        isDialogOpen={submitDialogOpen}
        onPrimaryButtonClick={handleConfirmSubmit}
        onSecondaryButtonClick={() => setSubmitDialogOpen(false)}
        handleDialogClose={() => setSubmitDialogOpen(false)}
        dialogTestId='order-form-confirm-submit-dialog'
        primaryButtonTestId='order-form-confirm-submit-button'
        secondaryButtonTestId='order-form-confirm-submit-cancel-button'
      />
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, DISCARD',
          secondaryButtonText: 'NO',
          title: 'Discard Draft?',
          subtitle:
            'Are you sure you want to discard this draft? This action cannot be undone.',
          type: FeedbackType.Error,
          icon: Trash,
        }}
        isDialogOpen={discardDialogOpen}
        onPrimaryButtonClick={handleConfirmDiscard}
        onSecondaryButtonClick={() => setDiscardDialogOpen(false)}
        handleDialogClose={() => setDiscardDialogOpen(false)}
      />
    </>
  );
};
